import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { FC, memo, useEffect, useMemo, useState } from 'react';

export type Props = {
  open: boolean;
  shipperNames: string[];
  isLoading: boolean;
  isError: boolean;
  barcodeSelectable?: boolean;
  onBack: () => void;
  onConfirm: (selected: string[], barcodeSelected?: string[]) => void;
};

const ShipperSelectDialog: FC<Props> = memo(({
  open,
  shipperNames,
  isLoading,
  isError,
  barcodeSelectable = false,
  onBack,
  onConfirm,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [barcodeSelected, setBarcodeSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setBarcodeSelected(new Set());
    }
  }, [open]);

  const allSelected = useMemo(
    () => shipperNames.length > 0 && shipperNames.every((name) => selected.has(name)),
    [shipperNames, selected],
  );

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
        setBarcodeSelected((barcodePrev) => {
          const barcodeNext = new Set(barcodePrev);
          barcodeNext.delete(name);
          return barcodeNext;
        });
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const toggleBarcode = (name: string) => {
    setBarcodeSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleConfirm = () => {
    if (barcodeSelectable) {
      onConfirm(Array.from(selected), Array.from(barcodeSelected));
    } else {
      onConfirm(Array.from(selected));
    }
  };

  return (
    <Dialog open={open} onClose={onBack} maxWidth="sm" fullWidth>
      <DialogTitle>荷主の選択</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          出力対象の荷主を選択してください（複数選択可）。
        </Typography>
        {barcodeSelectable && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            「バーコード」をONにした荷主は、伝票Noをバーコードで出力します。
          </Typography>
        )}
        {isLoading && <Typography>読み込み中...</Typography>}
        {isError && <Typography color="error">荷主一覧の取得に失敗しました。</Typography>}
        {!isLoading && !isError && shipperNames.length === 0 && (
          <Typography color="text.secondary">期間内に荷主が登録されていません。</Typography>
        )}
        {!isLoading && !isError && shipperNames.length > 0 && (
          <>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              {barcodeSelectable && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    py: 0.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>荷主</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, pr: 1 }}>バーコード</Typography>
                </Box>
              )}
              <List dense disablePadding>
                {shipperNames.map((name) => (
                  <ListItem key={name} sx={{ px: 2, py: 0.25, display: 'flex', justifyContent: 'space-between' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selected.has(name)}
                          onChange={() => toggle(name)}
                          size="small"
                        />
                      }
                      label={name}
                    />
                    {barcodeSelectable && (
                      <Checkbox
                        checked={barcodeSelected.has(name)}
                        disabled={!selected.has(name)}
                        inputProps={{ 'aria-label': `${name} バーコード` }}
                        onChange={() => toggleBarcode(name)}
                        size="small"
                      />
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onBack}>戻る</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={selected.size === 0}>
          出力へ進む
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default ShipperSelectDialog;
